import React, { ReactNode, useState, useEffect, useRef } from 'react';
import { Container } from 'react-bootstrap';

// Define the component's props interface (unchanged)
interface ParallaxProps {
    backgroundImageUrl: string;
    children: ReactNode;
    minHeight?: string;
}

const Parallax: React.FC<ParallaxProps> = ({
                                               backgroundImageUrl,
                                               children,
                                               minHeight = '100vh',
                                           }) => {
    // State to store the Y-offset for the image movement
    const [offsetY, setOffsetY] = useState(0);
    // Ref to target the container element to determine its position
    const containerRef = useRef<HTMLDivElement>(null);

    // Define the rate of movement (how much the image should move)
    const PARALLAX_RATE = 0.2; // Move the background at 20% of the scroll speed

    // 1. Scroll Handling Function
    const handleScroll = () => {
        if (!containerRef.current) return;

        // Calculate the container's position relative to the viewport
        const top = containerRef.current.getBoundingClientRect().top;

        // Calculate the center of the viewport (or any arbitrary reference point)
        const viewportCenter = window.innerHeight / 2;

        // Calculate the difference from the center, which gives us the scroll distance
        // The image movement should be calculated relative to when the container enters the viewport
        // We calculate how far the container's top edge is from the top of the viewport.
        const scrollPosition = top;

        // Apply the parallax rate to the scroll distance
        // The negative sign ensures the image moves in the opposite direction of the scroll
        const movement = scrollPosition * PARALLAX_RATE;

        setOffsetY(movement);
    };

    // 2. Set up and clean up the scroll event listener
    useEffect(() => {
        // We use the 'scroll' event on the window to track the scroll position
        window.addEventListener('scroll', handleScroll);

        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []); // Empty dependency array means this runs once on mount and once on unmount

    // Inline styles for the image movement
    const imageStyle: React.CSSProperties = {
        // Use the translateY CSS transform to move the image
        transform: `translateY(${offsetY}px)`,
    };

    // Inline styles for the main container
    const containerStyle: React.CSSProperties = {
        position: 'relative',
        minHeight: minHeight,
        overflow: 'hidden', // Important to hide the image when it moves out of its container bounds
    };


    return (
        <div ref={containerRef} style={containerStyle} className="bg-container">
            {/* The Parallax Background Layer */}
            {/* The image now receives the dynamic style for movement */}
            <img
                src={backgroundImageUrl}
                className="bg-fullscreen"
                alt="Background"
                style={imageStyle}
            />

            {/* The Content Layer (Children) */}
            {/* Use the Bootstrap Container to constrain the width of your content */}
            <div className="w-100 h-100 centered-caption-content">
                <Container fluid>{children}</Container>
            </div>
        </div>
    );
};

export default Parallax;